/**
 * ULIDs (PLAN §6.1): row ids and the `changedAt` catch-up cursor.
 *
 * 26 characters of Crockford base-32: 10 for a 48-bit millisecond timestamp,
 * 16 for 80 bits of randomness. Plain string order is time order, which is why
 * `changedAt` can be a DynamoDB sort key and a SQLite index without parsing.
 *
 * Randomness and the wall clock are parameters: this package is pure.
 */

const ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
/** The first character carries only 3 bits of the 48-bit time, so it is 0-7. */
const ULID = /^[0-7][0-9A-HJKMNP-TV-Z]{25}$/;
const TIME_CHARS = 10;
const RANDOM_CHARS = 16;
const RANDOM_BYTES = 10;
/** 2^48 - 1: the largest timestamp a ULID can carry (year 10889). */
export const MAX_ULID_MS = 2 ** 48 - 1;

/** Whether `value` is a canonical (upper-case) ULID. */
export function isValidUlid(value: string): boolean {
  return ULID.test(value);
}

function assertMs(ms: number): void {
  if (!Number.isInteger(ms) || ms < 0 || ms > MAX_ULID_MS) {
    throw new Error(`ULID timestamp out of range: ${String(ms)}`);
  }
}

function encodeTime(ms: number): string {
  let out = '';
  let rest = ms;
  for (let i = 0; i < TIME_CHARS; i += 1) {
    out = `${ALPHABET.charAt(rest % 32)}${out}`;
    rest = Math.floor(rest / 32);
  }
  return out;
}

/** 80 bits → 16 base-32 characters, most significant first. */
function encodeRandom(bytes: Uint8Array): string {
  let out = '';
  let buffer = 0;
  let bits = 0;
  for (const byte of bytes) {
    buffer = (buffer << 8) | byte;
    bits += 8;
    while (bits >= 5) {
      bits -= 5;
      out += ALPHABET.charAt((buffer >> bits) & 31);
    }
    buffer &= (1 << bits) - 1;
  }
  return out;
}

/** The millisecond timestamp a ULID carries. Throws on a malformed ULID. */
export function ulidTimeMs(ulid: string): number {
  if (!isValidUlid(ulid)) throw new Error(`Invalid ULID ${JSON.stringify(ulid)}`);
  let ms = 0;
  for (const char of ulid.slice(0, TIME_CHARS)) ms = ms * 32 + ALPHABET.indexOf(char);
  return ms;
}

/** Format a ULID from a timestamp and exactly 10 bytes of randomness. */
export function formatUlid(ms: number, randomness: Uint8Array): string {
  assertMs(ms);
  if (randomness.length !== RANDOM_BYTES) {
    throw new Error(`ULID randomness must be ${String(RANDOM_BYTES)} bytes, got ${String(randomness.length)}`);
  }
  return `${encodeTime(ms)}${encodeRandom(randomness)}`;
}

/**
 * The smallest ULID at `ms` — every ULID minted at or after `ms` sorts at or
 * above it. The `changedSince` overlap (PLAN §6.5) queries from
 * `minUlidAt(ulidTimeMs(cursor) - overlap)`.
 */
export function minUlidAt(ms: number): string {
  assertMs(ms);
  return `${encodeTime(ms)}${'0'.repeat(RANDOM_CHARS)}`;
}

/** The randomness part plus one, or null when it is all ones (overflow). */
function incrementRandom(random: Uint8Array): Uint8Array | null {
  const next = Uint8Array.from(random);
  for (let i = next.length - 1; i >= 0; i -= 1) {
    const byte = next[i] ?? 0;
    if (byte < 255) {
      next[i] = byte + 1;
      return next;
    }
    next[i] = 0;
  }
  return null;
}

/**
 * A monotonic ULID source (the ULID spec's monotonic mode): within one
 * millisecond — or while the wall clock stands still or steps back — each ULID
 * is the previous one plus one, so a writer's `changedAt` values are strictly
 * increasing and a cursor never skips a row this writer wrote.
 */
export function createUlidFactory(wallClockMs: () => number, randomBytes: (count: number) => Uint8Array): () => string {
  let lastMs = -1;
  let lastRandom: Uint8Array = new Uint8Array(RANDOM_BYTES);
  return () => {
    const physical = Math.floor(wallClockMs());
    if (physical > lastMs) {
      lastMs = physical;
      lastRandom = Uint8Array.from(randomBytes(RANDOM_BYTES));
    } else {
      const next = incrementRandom(lastRandom);
      if (next === null) {
        // 2^80 ULIDs inside one millisecond: roll into the next logical ms.
        lastMs += 1;
        lastRandom = new Uint8Array(RANDOM_BYTES);
      } else {
        lastRandom = next;
      }
    }
    return formatUlid(lastMs, lastRandom);
  };
}
